import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import styled from '@emotion/styled'
import { TimeIcon, ChevronRightIcon } from '@instacart/ids-core'
import {
  Badge,
  Card,
  colors,
  spacing,
  fontSize,
  fontWeight,
} from '@retailer-platform/shared-components'
import { useCoreContext } from '../../../../../utils/core/RPPCoreContext'
import { useCurrentAccountContext } from '../../../../../utils/current-account/CurrentAccountContext'
import { useLocalForageKey } from '../../../../../utils/local-storage/useLocalForageKey.hooks'
import { toSentenceCase } from '../../../../../utils/strings/toSentenceCase'
import { useDashMessages } from '../../../../../intl/intl.hooks'
import {
  buildAdminRecentlyViewedStorageKey,
  RECENTLY_VIEWED_DISPLAY_COUNT,
  RECENTLY_VIEWED_WINDOW_MS,
  type AdminPageView,
} from './recentlyViewed.types'
import { aggregateRecentlyViewed, prettifyPath } from './recentlyViewed.utils'
import { useAdminNavLabels } from './useAdminNavLabels'

const Container = styled.div({
  width: '100%',
  maxWidth: 520,
})

const List = styled.ul({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
})

const Row = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.X12,
  padding: `${spacing.X12} ${spacing.X8}`,
  borderRadius: spacing.X8,
  textDecoration: 'none',
  color: colors.GRAYSCALE.X70,
  transition: 'background-color 0.1s ease-in-out',
  '&:hover': {
    backgroundColor: colors.GRAYSCALE.X10,
  },
  '& + &': {
    borderTop: `1px solid ${colors.GRAYSCALE.X20}`,
  },
})

const RowText = styled.div({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.X4,
})

const Label = styled.span({
  fontSize: fontSize.X14,
  fontWeight: fontWeight.SEMIBOLD,
  color: colors.GRAYSCALE.X80,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const Path = styled.span({
  fontSize: fontSize.X12,
  color: colors.GRAYSCALE.X50,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const TitleRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.X8,
})

export const RecentlyViewedList = () => {
  const intl = useIntl()
  const { routesByName } = useCoreContext()
  const navLabels = useAdminNavLabels()
  const identityId = useCurrentAccountContext()?.account?.canonicalId
  const { data } = useLocalForageKey<AdminPageView[]>(
    buildAdminRecentlyViewedStorageKey(identityId)
  )

  const i18n = useDashMessages({
    title: 'app.admin.home.recentlyViewed.title',
    subtitle: 'app.admin.home.recentlyViewed.subtitle',
  })

  const entries = useMemo(() => {
    if (!Array.isArray(data)) return []
    // Don't list the admin home in its own list.
    const homeKey = routesByName['app-admin'] ?? '/app/admin'
    return aggregateRecentlyViewed(
      data,
      Date.now(),
      RECENTLY_VIEWED_WINDOW_MS,
      RECENTLY_VIEWED_DISPLAY_COUNT,
      new Set([homeKey])
    )
  }, [data, routesByName])

  if (entries.length === 0) return null

  return (
    <Container>
      <Card
        title={
          <TitleRow>
            <TimeIcon size={18} css={{ fill: colors.GRAYSCALE.X60 }} />
            <span>{i18n.title}</span>
          </TitleRow>
        }
        subtitle={i18n.subtitle}
      >
        <List>
          {entries.map(entry => {
            const labelId = entry.routeName ? navLabels.get(entry.routeName) : undefined
            const label = labelId
              ? toSentenceCase(intl.formatMessage({ id: labelId }))
              : toSentenceCase(prettifyPath(entry.key))

            return (
              <Row key={entry.key} to={entry.path}>
                <RowText>
                  <Label>{label}</Label>
                  <Path>{entry.path}</Path>
                </RowText>
                <Badge color="grey">
                  {intl.formatMessage(
                    { id: 'app.admin.home.recentlyViewed.viewCount' },
                    { count: entry.count }
                  )}
                </Badge>
                <ChevronRightIcon size={16} css={{ flexShrink: 0, fill: colors.GRAYSCALE.X30 }} />
              </Row>
            )
          })}
        </List>
      </Card>
    </Container>
  )
}
